import React, { useState, useEffect, Fragment } from 'react'

import MultiSelect from '@khanacademy/react-multi-select'

import { bool, string } from 'prop-types'

import ActiveFilters from 'Pods/Profile/Products/Listing/ActiveFilters'
import Pagination from 'Pods/Profile/Products/Listing/Pagination'
import Loader from 'Pods/Shared/Loader'
import SharedTable from 'Pods/Shared/Table/SharedTable'

import JsonToQuery from 'Utils/JsonToQuery'
import QueryToJson from 'Utils/QueryToJson'

import usePurchases from '../../Hooks/UsePurchases'
import useSubscriptionTracking from '../../Hooks/useSubscriptionTracking'
import { PURCHASE_TABS } from '../../Static/constants'
import { defaultFilters, defaultTable, statuses } from '../../Static/DefaultValues'
import ProductActions from '../ProductActions'
import ProductInfo from '../ProductInfo'
import ProductPrice from '../ProductPrice'
import ProductRate from '../ProductRate'
import QueryField from '../QueryField'
import SubscriptionActions from '../SubscriptionTab/SubscriptionActions'

function PurchaseList({
  syncUrlState = true, itemsUrl, typeFilter = null,
  isYahaha = false, modelId = null, rating = null,
}) {
  const [currentFilters, setCurrentFilters] = useState(defaultFilters)
  const [{ data, isLoading }, setUrl] = usePurchases()
  const { trackDownloadClick } = useSubscriptionTracking()

  useEffect(() => {
    getInitialFilters()
  }, [])

  function getInitialFilters() {
    if (syncUrlState) {
      const firstLoadFlag = 'first=true'
      const currentSearchParams = window.location.search
      const pageParams = currentSearchParams
        ? `${currentSearchParams}&${firstLoadFlag}`
        : `?${firstLoadFlag}`
      const params = QueryToJson(currentSearchParams.substr(1))

      delete params.type

      setUrl(`/api/internal/profile/purchases${withTypeParam(pageParams)}`)

      if (!Object.keys(params).length) {
        return setCurrentFilters(defaultFilters)
      }

      return setCurrentFilters({ ...defaultFilters, ...params })
    }

    setCurrentFilters(defaultFilters)
    return setUrl(`/api/internal/profile/purchases${withTypeParam('?first=true')}`)
  }

  function updateFilters(filters, searchParams) {
    setCurrentFilters(filters)

    if (syncUrlState) {
      window.history.pushState({}, document.title, `${window.location.pathname}${withTypeParam(searchParams)}`)
    }
    setUrl(`/api/internal/profile/purchases${withTypeParam(searchParams)}`)
  }

  function getSearchParams(updatedFilters) {
    const truthyFilters = Object.entries(updatedFilters)
      .filter(([, val]) => !!val)
    const newFilters = Object.fromEntries(truthyFilters)

    return truthyFilters.length ? `?${JsonToQuery(newFilters)}` : ''
  }

  function withTypeParam(searchParams) {
    if (!typeFilter) return searchParams

    const queryParams = new URLSearchParams(searchParams.replace(/^\?/, ''))
    queryParams.set('type', typeFilter)

    return `?${queryParams.toString()}`
  }

  function handleFiltersChange(name, value) {
    if (currentFilters[name] === value) {
      return null
    }

    const updatedFilters = {
      ...currentFilters,
      page: 1,
      [name]: value,
    }

    return updateFilters(updatedFilters, getSearchParams(updatedFilters))
  }

  function removeFilter(filterValue, filterName) {
    const newState = currentFilters
    newState.page = 1

    if (filterName === 'query') {
      newState[filterName] = currentFilters[filterName].replace(filterValue, '').trim()
    } else {
      newState[filterName] = currentFilters[filterName].filter((i) => i !== filterValue)
    }

    return updateFilters(newState, getSearchParams(newState))
  }

  function clearFilters() {
    updateFilters(defaultFilters, '')
  }

  function renderPlaceholder() {
    return (
      <div className="u-text-center no-purchases-message">
        <span>You have no purchased items for this search query.</span>
        {'Refine your search and try again, or '}
        <a href={itemsUrl}>browse all models</a>.
      </div>
    )
  }

  function makeTable(currentItems) {
    const rows = currentItems.map(({ id, attributes }) => ({
      id,
      columns: [{
        content: <ProductInfo storeCreditsUsed {...attributes} />,
      }, {
        content: <ProductPrice {...attributes} />,
      }, {
        content: <ProductRate {...attributes} modelId={modelId} rating={rating} />,
      }, {
        content: attributes.isSubscriptionItem
          ? (
            <SubscriptionActions
              {...attributes}
              itemId={id}
              onDownloadClick={trackDownloadClick}
            />
          )
          : <ProductActions {...attributes} isYahaha={isYahaha} />,
      }],
    }))

    return { ...defaultTable, rows }
  }

  if (!data || isLoading) {
    return <Loader />
  }

  const { data: tableData, meta } = data || {}
  const showStatusFilter = typeFilter !== PURCHASE_TABS.assigned

  return (
    <Fragment>
      <div className="cgt-mb-10 row">
        <div className={showStatusFilter ? 'col-12 col-md-9' : 'col-12'}>
          <QueryField
            query={currentFilters.query}
            search={handleFiltersChange}
            isDisabled={isLoading}
          />
        </div>

        {showStatusFilter && (
          <div className="col-12 col-md-3">
            <div className="cgt-mb-10 custom-dropdown--full-width">
              <div className="search-form">
                <div className="input-container">
                  <MultiSelect
                    options={statuses}
                    onSelectedChanged={(value) => {
                      handleFiltersChange('status', value)
                    }}
                    selected={currentFilters.status}
                    placeholder="Status"
                    disableSearch
                    hasSelectAll={false}
                    disabled={isLoading}
                  />
                </div>
              </div>
            </div>
          </div>
        )}
        <div className="col-12">
          <ActiveFilters
            removeFilter={removeFilter}
            filters={currentFilters}
            clearFilters={clearFilters}
          />
        </div>
      </div>
      <div className="table-responsive table-responsive--default legacy-responsive-table">
        <SharedTable table={makeTable(tableData)} renderPlaceholder={renderPlaceholder} />
      </div>

      <div className="cgt-mb-10">
        <Pagination
          hidePerPage
          filters={{
            ...currentFilters,
            page: Number(currentFilters.page),
          }}
          onFiltersChange={({ page }) => {
            handleFiltersChange('page', page)
          }}
          meta={meta}
        />
      </div>
    </Fragment>
  )
}

PurchaseList.propTypes = {
  syncUrlState: bool,
  itemsUrl: string.isRequired,
  typeFilter: string,
  isYahaha: bool,
  modelId: string,
  rating: string,
}

export default PurchaseList
